import React from "react";
import {
  Bell, Moon, Sun, LogOut, User,
  CheckCircle2, AlertTriangle, XCircle, Info,
  CheckCheck, ExternalLink, UserCog, X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNotifications, useUnreadCount, useMarkAllRead, useMarkRead } from "@/hooks/useNotifications";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { useTheme } from "next-themes";
import { Link, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import type { Notification } from "@/types/database";

// ─── Icon / colour maps ────────────────────────────────────────────────────────

const iconMap: Record<string, React.ElementType> = {
  success: CheckCircle2,
  error:   XCircle,
  warning: AlertTriangle,
  info:    Info,
};

const colorMap: Record<string, string> = {
  success: "text-green-500",
  error:   "text-destructive",
  warning: "text-amber-500",
  info:    "text-blue-500",
};

const borderMap: Record<string, string> = {
  success: "border-l-green-500",
  error:   "border-l-destructive",
  warning: "border-l-amber-500",
  info:    "border-l-blue-500",
};

// ─── Relative time helper ─────────────────────────────────────────────────────

function formatRelativeTime(dateString: string): string {
  const date    = new Date(dateString);
  const now     = new Date();
  const diffMs  = now.getTime() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHr  = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHr  / 24);

  if (diffSec < 60)  return "Just now";
  if (diffMin < 60)  return `${diffMin}m ago`;
  if (diffHr  < 24)  return `${diffHr}h ago`;
  if (diffDay < 7)   return `${diffDay}d ago`;
  return date.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
}

// ─── Single notification row inside the dropdown ──────────────────────────────

function NotificationDropdownRow({
  n,
  onRead,
  onClose,
}: {
  n: Notification;
  onRead: (id: string) => void;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const Icon     = iconMap[n.type] || Info;
  const navLink  = n.data?.link as string | undefined;

  const handleClick = () => {
    if (!n.read) onRead(n.id);
    onClose();
    if (navLink) navigate(navLink);
  };

  return (
    <div
      className={`
        flex items-start gap-3 px-3 py-2.5 cursor-pointer
        hover:bg-muted/50 transition-colors
        border-l-2
        ${!n.read ? (borderMap[n.type] ?? "border-l-primary") : "border-l-transparent"}
        ${!n.read ? "bg-primary/[0.03]" : ""}
      `}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && handleClick()}
    >
      {/* Type icon */}
      <span className={`mt-0.5 shrink-0 ${colorMap[n.type] ?? "text-info"}`}>
        <Icon className="h-3.5 w-3.5" />
      </span>

      {/* Body */}
      <div className="flex-1 min-w-0 space-y-0.5">
        <div className="flex items-center gap-1.5 flex-wrap">
          <p className="text-xs font-medium leading-tight truncate max-w-[170px]">{n.title}</p>
          {!n.read && (
            <Badge className="h-3.5 px-1 text-[10px] bg-primary text-primary-foreground shrink-0">
              New
            </Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{n.message}</p>
      </div>

      {/* Right column */}
      <div className="flex flex-col items-end gap-1 shrink-0">
        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
          {formatRelativeTime(n.created_at)}
        </span>
        {navLink && (
          <span aria-label="Opens page" className="flex items-center">
            <ExternalLink className="h-2.5 w-2.5 text-muted-foreground/50" aria-hidden="true" />
          </span>
        )}
      </div>
    </div>
  );
}

// ─── Notification Bell Dropdown ───────────────────────────────────────────────

function NotificationBell() {
  const navigate = useNavigate();
  const { data: unreadCount = 0 }       = useUnreadCount();
  const { data: notifications = [], isLoading } = useNotifications();
  const markAllRead = useMarkAllRead();
  const markRead    = useMarkRead();

  // Keep only the 8 most recent for the dropdown preview
  const preview = (notifications as Notification[]).slice(0, 8);

  // We control open state so we can close on item click
  const [open, setOpen] = React.useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-8 w-8"
          aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications"}
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              aria-hidden="true"
              className="absolute -right-1 -top-1 h-4 min-w-[16px] justify-center rounded-full px-1 text-[10px]"
            >
              {unreadCount}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[320px] p-0" sideOffset={8}>
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-2 border-b border-border">
          <DropdownMenuLabel className="p-0 text-sm font-semibold">
            Notifications
            {unreadCount > 0 && (
              <Badge variant="secondary" className="ml-2 h-4 px-1.5 text-[10px]">
                {unreadCount} new
              </Badge>
            )}
          </DropdownMenuLabel>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
              onClick={(e) => {
                e.preventDefault();
                markAllRead.mutate();
              }}
              disabled={markAllRead.isPending}
            >
              <CheckCheck className="h-3 w-3" />
              Mark all read
            </Button>
          )}
        </div>

        {/* List */}
        {isLoading ? (
          <div className="px-3 py-3 space-y-2">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : preview.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center px-4">
            <Bell className="h-8 w-8 text-muted-foreground/30 mb-2" />
            <p className="text-xs font-medium text-muted-foreground">You're all caught up!</p>
            <p className="text-[11px] text-muted-foreground/70 mt-0.5">
              No new notifications right now.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[360px]">
            <div className="divide-y divide-border">
              {preview.map((n) => (
                <NotificationDropdownRow
                  key={n.id}
                  n={n}
                  onRead={(id) => markRead.mutate(id)}
                  onClose={() => setOpen(false)}
                />
              ))}
            </div>
          </ScrollArea>
        )}

        {/* Footer */}
        <div className="border-t border-border px-3 py-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full h-7 text-xs text-muted-foreground hover:text-foreground justify-center gap-1.5"
            onClick={() => {
              setOpen(false);
              navigate("/notifications");
            }}
          >
            View all notifications
            <ExternalLink className="h-3 w-3" />
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ─── Role Impersonator (system_admin only) ────────────────────────────────────

const IMPERSONABLE_ROLES = [
  { value: "strata_admin",      label: "Strata Admin" },
  { value: "property_manager",  label: "Property Manager" },
  { value: "host",              label: "Host" },
  { value: "individual",        label: "Individual" },
] as const;

function RoleImpersonator() {
  const { user, devRole, setDevRole } = useAuth();
  const navigate = useNavigate();
  if (user?.role !== "system_admin") return null;

  return (
    <div className="flex items-center gap-2">
      {devRole ? (
        <div className="flex items-center gap-1.5 rounded-full bg-orange-500/15 border border-orange-500/30 px-3 py-1">
          <UserCog className="h-3 w-3 text-orange-500 shrink-0" />
          <button
            type="button"
            onClick={() => navigate("/admin")}
            className="text-[11px] font-medium text-orange-600 capitalize whitespace-nowrap hover:text-orange-700 transition-colors"
          >
            Viewing as {devRole.replace("_", " ")}
          </button>
          <button
            type="button"
            onClick={() => { setDevRole(null); navigate("/admin"); }}
            className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-orange-500/20 text-orange-500 transition-colors"
            aria-label="Exit impersonation"
          >
            <X className="h-2.5 w-2.5" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5">
          <UserCog className="h-3.5 w-3.5 text-muted-foreground" />
          <Select onValueChange={(v) => setDevRole(v as typeof IMPERSONABLE_ROLES[number]["value"])}>
            <SelectTrigger className="h-7 w-[130px] text-xs border-dashed">
              <SelectValue placeholder="View as role…" />
            </SelectTrigger>
            <SelectContent>
              {IMPERSONABLE_ROLES.map((r) => (
                <SelectItem key={r.value} value={r.value} className="text-xs">
                  {r.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}

// ─── Top Header ───────────────────────────────────────────────────────────────

export function TopHeader() {
  const { user, logout } = useAuth();
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (err: any) {
      toast({
        title: "Logout failed",
        description: err?.message ?? "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <header className="flex h-14 items-center gap-3 border-b border-border bg-card px-4">
      <SidebarTrigger />

      {/* Brand wordmark — clicking goes to dashboard */}
      <Link to="/dashboard" className="flex items-center gap-2 group" aria-label="Stratakey dashboard">
        <img
          src="/logo.png"
          alt="Stratakey"
          className="h-6 w-auto object-contain rounded-lg group-hover:scale-105 transition-transform"
        />
        <span className="text-sm font-semibold tracking-tight text-foreground/80 select-none group-hover:text-foreground transition-colors">
          Stratakey
        </span>
      </Link>

      <div className="flex-1" />

      {/* Role impersonator — system_admin only */}
      <RoleImpersonator />

      {/* Theme toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
        onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      >
        <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" aria-hidden="true" />
        <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" aria-hidden="true" />
      </Button>

      {/* Notification bell dropdown */}
      <NotificationBell />

      {/* User menu */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 gap-2 px-2" aria-label="User menu">
            <div aria-hidden="true" className="flex h-6 w-6 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
              {user?.fullName?.split(" ").map((n) => n[0]).join("") ?? "?"}
            </div>
            <span className="text-xs font-medium whitespace-nowrap">{user?.fullName}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
          <DropdownMenuLabel className="text-xs text-muted-foreground capitalize font-normal -mt-1">
            {user?.role?.replace("_", " ")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-xs" onClick={() => navigate("/profile")}>
            <User className="mr-2 h-3 w-3" /> My Profile
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className="text-xs" onClick={handleLogout}>
            <LogOut className="mr-2 h-3 w-3" /> Log out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
